import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  Avatar,
  Alert,
  Chip,
} from "@mui/material";
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import MetricCard from "../shared/MetricCard";
import ChartContainer from "../shared/ChartContainer";
import dashboardService from "../../../../../services/dashboardService";

const TENURE_COLORS = ["#64B5F6", "#4CAF50", "#FF9800", "#9C27B0"];

const BusinessModule = ({ organizationId }) => {
  const theme = useTheme();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-business", organizationId],
    queryFn: () => dashboardService.getBusinessMetrics(organizationId, true),
    staleTime:            60 * 60 * 1000, // 1 hour — BQ views refresh daily
    gcTime:               4 * 60 * 60 * 1000, // 4 hours — keep across a full session
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });

  const biz = data || {};
  const kpis           = biz.kpis           || {};
  const tenureSegments = biz.tenureSegments  || [];
  const pipeline       = biz.pipeline        || [];
  const coachWorkload  = biz.coachWorkload   || [];

  const totalActive = kpis.totalActive || 0;

  // Net growth this month
  const netGrowth = (kpis.newThisMonth || 0) - (kpis.churnedThisMonth || 0);

  // Tenure donut data — filter out zero segments
  const tenureData = tenureSegments.filter(s => s.count > 0);

  return (
    <Box sx={{ p: 0 }}>
      {/* KPI Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Clients"
            value={kpis.totalActive}
            icon={<PeopleIcon sx={{ fontSize: 28 }} />}
            loading={isLoading}
            subtitle="currently enrolled"
            description="Total clients with status = active and onboarding complete."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="New This Month"
            value={kpis.newThisMonth}
            icon={<PersonAddIcon sx={{ fontSize: 28 }} />}
            variant="success"
            loading={isLoading}
            subtitle="clients onboarded"
            description="Clients whose account was created in the current calendar month."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Churned This Month"
            value={kpis.churnedThisMonth}
            icon={<PersonRemoveIcon sx={{ fontSize: 28 }} />}
            variant={kpis.churnedThisMonth > 0 ? "warning" : "success"}
            loading={isLoading}
            subtitle="clients deactivated"
            description="Clients whose status changed from active to inactive in the current month."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Client Tenure"
            value={kpis.avgTenureMonths}
            format="number"
            icon={<ScheduleIcon sx={{ fontSize: 28 }} />}
            loading={isLoading}
            subtitle="months with you"
            description="Average number of months active clients have been enrolled, from their account creation date."
          />
        </Grid>
      </Grid>

      {/* Net growth callout + Operational gaps */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: "100%",
              boxShadow: theme.shadows[2],
              borderLeft: 4,
              borderLeftColor: netGrowth >= 0 ? theme.palette.success.main : theme.palette.error.main,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
                Net Growth This Month
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: netGrowth >= 0 ? theme.palette.success.main : theme.palette.error.main }}>
                {netGrowth >= 0 ? `+${netGrowth}` : netGrowth}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {kpis.newThisMonth || 0} added · {kpis.churnedThisMonth || 0} churned
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", boxShadow: theme.shadows[2] }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5, mb: 2 }}>
                Operational Gaps
              </Typography>
              {(kpis.noProtocolCount || 0) === 0 && (kpis.notOnboardedCount || 0) === 0 ? (
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                  ✓ All active clients have protocols and are onboarded
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {(kpis.noProtocolCount || 0) > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                      <Typography variant="body2">
                        <strong>{kpis.noProtocolCount}</strong> active client{kpis.noProtocolCount !== 1 ? "s" : ""} without an assigned training plan
                      </Typography>
                    </Box>
                  )}
                  {(kpis.notOnboardedCount || 0) > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                      <Typography variant="body2">
                        <strong>{kpis.notOnboardedCount}</strong> client{kpis.notOnboardedCount !== 1 ? "s" : ""} not yet onboarded to the app
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pipeline chart + Tenure donut */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <ChartContainer
            title="12-Month Client Pipeline"
            subtitle="New clients added vs clients churned per month"
            loading={isLoading}
            error={error}
            description="Monthly count of new client accounts created vs clients deactivated. Derived from account creation dates and status change events."
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={pipeline} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  dataKey="month_label"
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  tickFormatter={v => v.split(" ")[0]} // Show just "Jan", "Feb", etc.
                />
                <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} allowDecimals={false} />
                <RechartsTooltip
                  contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="new_clients"     name="New"     fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
                <Bar dataKey="churned_clients" name="Churned" fill={theme.palette.error.light}  radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartContainer
            title="Client Tenure"
            subtitle="How long clients have been enrolled"
            loading={isLoading}
            error={error}
            description="Segments active clients by how long they've been enrolled. New < 30 days, Building 30–90 days, Established 90 days–1 year, Long-term > 1 year."
          >
            {tenureData.length > 0 ? (
              <Box>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={tenureData}
                      dataKey="count"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={65}
                      innerRadius={35}
                    >
                      {tenureData.map((_, i) => (
                        <Cell key={i} fill={TENURE_COLORS[i % TENURE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                      formatter={(val, name) => [`${val} clients`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {tenureData.map((seg, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: TENURE_COLORS[i % TENURE_COLORS.length] }} />
                      <Typography variant="caption" color="text.secondary">
                        {seg.label} ({seg.count})
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
                No tenure data available
              </Typography>
            )}
          </ChartContainer>
        </Grid>
      </Grid>

      {/* Coach Workload */}
      {coachWorkload.length > 0 && (
        <ChartContainer
          title="Coach Workload"
          subtitle="Active clients assigned per coach"
          loading={isLoading}
          error={error}
          description="Number of active clients whose primary coach is each coach. Based on the first entry in the client's coaches array."
        >
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {coachWorkload.map(coach => (
              <Grid item xs={12} sm={6} md={4} key={coach.coachId}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: theme.palette.action.hover,
                  }}
                >
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 44, height: 44 }}>
                    {(coach.firstName?.[0] || "?").toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {coach.firstName} {coach.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {coach.clientCount} client{coach.clientCount !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: "auto" }}>
                    <Chip
                      label={`${Math.round((coach.clientCount / (totalActive || 1)) * 100)}%`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </ChartContainer>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => refetch()}>
          Failed to load business data. Click to retry.
        </Alert>
      )}
    </Box>
  );
};

export default BusinessModule;
